import React, { useState } from "react";
import {
  Grid,
  Card,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  Button,
  Stack,
} from "@mui/material";
import Loader from "../../../Loader/loader";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { ChangeAdminPasswordRequest } from "../../../api/api";

function ChangePasswordForm() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validationSchema = Yup.object({
    current_password: Yup.string().required("Current password is required"),
    new_password: Yup.string()
      .min(6, "Password should be at least 6 characters")
      .required("New password is required"),
    new_password_confirmation: Yup.string()
      .oneOf([Yup.ref("new_password")], "Passwords must match")
      .required("Please confirm your password"),
  });

  const formik = useFormik({
    initialValues: {
      current_password: "",
      new_password: "",
      new_password_confirmation: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        setIsSubmitting(true);
        const token = localStorage.getItem("authToken");

        await ChangeAdminPasswordRequest(
          {
            current_password: values.current_password,
            new_password: values.new_password,
            new_password_confirmation: values.new_password_confirmation,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        resetForm();
        navigate("/sign-in");
      } catch (error) {
        console.error("Password change error:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={4}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={6} lg={5}>
            <Card sx={{ padding: 3 }}>
              <MDTypography variant="h5" fontWeight="bold" mb={2}>
                Change Password
              </MDTypography>

              {isSubmitting ? (
                <Grid container justifyContent="center" alignItems="center" sx={{ py: 5 }}>
                  <Loader />
                </Grid>
              ) : (
                <form onSubmit={formik.handleSubmit}>
                  <Grid container spacing={3}>
                    {/* Current Password */}
                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="current_password">Current Password</InputLabel>
                        <OutlinedInput
                          type="password"
                          id="current_password"
                          name="current_password"
                          fullWidth
                          value={formik.values.current_password}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.current_password &&
                            Boolean(formik.errors.current_password)
                          }
                        />
                        {formik.touched.current_password && formik.errors.current_password && (
                          <FormHelperText error>{formik.errors.current_password}</FormHelperText>
                        )}
                      </Stack>
                    </Grid>

                    {/* New Password */}
                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="new_password">New Password</InputLabel>
                        <OutlinedInput
                          type="password"
                          id="new_password"
                          name="new_password"
                          fullWidth
                          value={formik.values.new_password}
                          onChange={formik.handleChange}
                          error={formik.touched.new_password && Boolean(formik.errors.new_password)}
                        />
                        {formik.touched.new_password && formik.errors.new_password && (
                          <FormHelperText error>{formik.errors.new_password}</FormHelperText>
                        )}
                      </Stack>
                    </Grid>

                    {/* Confirm Password */}
                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="new_password_confirmation">
                          Confirm New Password
                        </InputLabel>
                        <OutlinedInput
                          type="password"
                          id="new_password_confirmation"
                          name="new_password_confirmation"
                          fullWidth
                          value={formik.values.new_password_confirmation}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.new_password_confirmation &&
                            Boolean(formik.errors.new_password_confirmation)
                          }
                        />
                        {formik.touched.new_password_confirmation &&
                          formik.errors.new_password_confirmation && (
                            <FormHelperText error>
                              {formik.errors.new_password_confirmation}
                            </FormHelperText>
                          )}
                      </Stack>
                    </Grid>

                    {/* Submit Button */}
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        sx={{
                          backgroundColor: "#1A73E8",
                          color: "#FFFFFF",
                          fontWeight: "bold",
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: "#1669C1",
                          },
                        }}
                      >
                        Change Password
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default ChangePasswordForm;
